import type { MapAggregate } from "../domain/types.ts";
import { closeIcon, copyIcon } from "./icons.ts";
import { element } from "./dom.ts";

export type AggregatePanelOptions = {
  readonly panel: HTMLElement;
  readonly aggregate: MapAggregate;
  readonly onClose: () => void;
  readonly coverage?: AggregateDetailCoverage;
};

export type AggregateDetailCoverage = { readonly loaded: number; readonly composed: number; readonly visible: number };

export type AggregatePanelSummary = {
  readonly stats: readonly { readonly label: string; readonly value: string }[];
  readonly coverage: string | null;
};

export function aggregatePanelSummary(aggregate: MapAggregate, coverage?: AggregateDetailCoverage): AggregatePanelSummary {
  const confidence = { label: "vertrouwen", value: `${Math.round(aggregate.confidence * 100)}%` };
  if (aggregate.level === "macro") {
    return {
      stats: [{ label: "uitspraken in dit gebied", value: aggregate.memberCount.toLocaleString("nl-NL") }, confidence],
      coverage: null,
    };
  }
  const total = aggregate.memberCount.toLocaleString("nl-NL");
  const anchors = aggregate.tierZeroCount.toLocaleString("nl-NL");
  return {
    stats: [
      { label: "uitspraken in dit thema", value: total },
      { label: "netwerkankers in de detailkaart", value: anchors },
      confidence,
    ],
    coverage: coverage === undefined
      ? `Binnen deze ${total} uitspraken zijn ${anchors} netwerkankers geselecteerd; ankers zijn de kern van het thema, niet het totale aantal uitspraken.`
      : `${total} uitspraken totaal · ${coverage.loaded.toLocaleString("nl-NL")} geladen · ${coverage.composed.toLocaleString("nl-NL")} in de kaart · ${coverage.visible.toLocaleString("nl-NL")} zichtbaar. Daarvan zijn ${anchors} netwerkankers.`,
  };
}

export function updateAggregatePanelCoverage(
  panel: HTMLElement,
  aggregate: MapAggregate,
  coverage: AggregateDetailCoverage,
): void {
  panel.dataset["loadedMembers"] = String(coverage.loaded);
  panel.dataset["composedJudgments"] = String(coverage.composed);
  panel.dataset["visibleJudgments"] = String(coverage.visible);
  panel.dataset["countLoaded"] = String(coverage.loaded);
  panel.dataset["countComposed"] = String(coverage.composed);
  panel.dataset["countVisible"] = String(coverage.visible);
  const copy = aggregatePanelSummary(aggregate, coverage).coverage;
  const field = panel.querySelector<HTMLElement>('[data-field="network-coverage"]');
  if (field !== null && copy !== null) field.textContent = copy;
}

export function clearAggregatePanelMetadata(panel: HTMLElement): void {
  for (const key of [
    "totalMembers", "anchorMembers", "loadedMembers", "composedJudgments", "visibleJudgments",
    "countTotal", "countAnchors", "countLoaded", "countComposed", "countVisible",
  ]) delete panel.dataset[key];
}

function fact(label: string, value: string): HTMLElement {
  return element("div", { class: "stat" }, [element("b", {}, [value]), element("span", {}, [label])]);
}

export function renderAggregatePanel(options: AggregatePanelOptions): void {
  const { panel, aggregate, onClose } = options;
  const summary = aggregatePanelSummary(aggregate, options.coverage);
  panel.classList.add("open");
  panel.dataset["kind"] = "aggregate";
  panel.dataset["totalMembers"] = String(aggregate.memberCount);
  panel.dataset["anchorMembers"] = String(aggregate.tierZeroCount);
  panel.dataset["countTotal"] = String(aggregate.memberCount);
  panel.dataset["countAnchors"] = String(aggregate.tierZeroCount);
  if (options.coverage !== undefined) {
    updateAggregatePanelCoverage(panel, aggregate, options.coverage);
  } else {
    delete panel.dataset["loadedMembers"];
    delete panel.dataset["composedJudgments"];
    delete panel.dataset["visibleJudgments"];
    delete panel.dataset["countLoaded"];
    delete panel.dataset["countComposed"];
    delete panel.dataset["countVisible"];
  }
  const close = element("button", { class: "close", type: "button", "aria-label": "Details sluiten" }, [closeIcon()]);
  close.addEventListener("click", onClose);
  const expand = element("button", { type: "button" }, [aggregate.level === "macro" ? "Toon thema’s" : "Toon uitspraken"]);
  expand.addEventListener("click", () => expand.dispatchEvent(new CustomEvent("atlas:expand-aggregate", { bubbles: true, detail: { id: aggregate.id.value } })));
  const copy = element("button", { type: "button" }, [copyIcon(), "Kopieer beeld"]);
  copy.addEventListener("click", () => copy.dispatchEvent(new CustomEvent("atlas:copy-view", { bubbles: true })));
  panel.replaceChildren(
    element("header", {}, [element("div", {}, [element("p", { class: "panel-kicker" }, [aggregate.level === "macro" ? "Juridisch gebied" : "Thema"]), element("h2", {}, [aggregate.label])]), close]),
    element("p", { class: "metadata" }, ["Automatisch label uit citatiepatronen en abstracts; geen officiële taxonomie."]),
    element("div", { class: "stats" }, summary.stats.map((item) => fact(item.label, item.value))),
    ...(summary.coverage === null ? [] : [element("p", { class: "metadata", "data-field": "network-coverage" }, [summary.coverage])]),
    element("h3", {}, ["Onderbouwende termen"]),
    element("p", { class: "evidence-terms" }, [aggregate.evidenceTerms.join(" · ") || "Onvoldoende abstracttekst; label is een terugvalwaarde."]),
    element("h3", {}, ["Gerechten en periode"]),
    element("p", { class: "metadata" }, [
      `${aggregate.yearMin || "?"}–${aggregate.yearMax || "?"} · ${aggregate.courtMix.slice(0, 3).map((item) => `${item.court} ${item.count.toLocaleString("nl-NL")}`).join(" · ")}`,
    ]),
    element("h3", {}, ["Samengevoegde citatiestromen"]),
    element("p", { class: "metadata" }, ["Stromen verbinden thema’s als totalen. Ze stellen geen afzonderlijke uitspraak-tot-uitspraakverbinding voor."]),
    element("div", { class: "stats" }, [fact("inkomend gewicht", aggregate.totalInDegree.toLocaleString("nl-NL")), fact("uitgaand gewicht", aggregate.totalOutDegree.toLocaleString("nl-NL"))]),
    element("div", { class: "actions" }, [expand, copy]),
  );
}
